import { brands, type Brand } from './brands';
import { validateAppConfig, type AppConfig } from './appConfigSchema.mts';

// Build-level values: they describe this bundle, not the environment it is deployed to.
const gitHash = import.meta.env.GIT_HASH || 'LOCAL';
const devTheme = import.meta.env.DEV_THEME || '';
const sentryRelease = import.meta.env.SENTRY_RELEASE || '';

const defaultBrand = brands.find(b => b.origin === 'GRID') ?? brands[0];

function resolveBrand(): Brand {
  const host = window.location.hostname.toLowerCase();

  // Local always uses grid.local.lifeway.com — theme comes from DEV_THEME at serve time.
  if (devTheme) {
    return brands.find(b => b.theme === devTheme) ?? defaultBrand;
  }

  // Prefer non-GRID matches so "ministrygrid" does not win over white-label hosts.
  const whiteLabel = brands.find(b => b.origin !== 'GRID' && b.hostIncludes && host.includes(b.hostIncludes));

  return whiteLabel ?? defaultBrand;
}

// Must run before any app module is imported; several read window.config at module load.
function installAppConfig(appConfig: AppConfig): Brand {
  const resolvedBrand = resolveBrand();
  const appEnv = appConfig.ENV;

  // Use window.getApiUrl() / getAccountUrl() rather than API_URL / LIFEWAY_ACCOUNT_URL: they apply the host overrides.
  window.config = {
    ...appConfig,
    SENTRY_RELEASE: sentryRelease,
    // Only Grid is tracked in FullStory, and only the prod config sets the id.
    FULLSTORY_ORG_ID: resolvedBrand.origin === 'GRID' ? appConfig.FULLSTORY_ORG_ID : '',
    activeCampaignEnabled: appEnv === 'PROD' && resolvedBrand.origin === 'GRID',
  };

  window.getApiUrl = function () {
    const API_URL_HOST_OVERRIDE: Record<string, string> = {
      'bethlehem.stage.lifeway.com': 'https://ministrygrid.lifeway.com',
      'training.connexuschurch.com': 'https://training.connexuschurch.com',
      'training.centrikid.lifeway.com': 'https://training.centrikid.lifeway.com',
      'training.fuge.lifeway.com': 'https://training.fuge.lifeway.com',
      'training.studentlife.lifeway.com': 'https://training.studentlife.lifeway.com',
      'training.sbc.net': 'https://training.sbc.net',
    };

    const base = API_URL_HOST_OVERRIDE[window.location.hostname.toLowerCase()] || appConfig.API_URL;
    return base + '/api';
  };

  window.getAccountUrl = function () {
    if (window.location.hostname.includes('bethlehem.stage')) {
      return 'https://account.lifeway.com';
    }
    return appConfig.LIFEWAY_ACCOUNT_URL;
  };

  window.getCommitHash = function () {
    return gitHash;
  };

  window.getOrigin = function () {
    return resolvedBrand.origin || 'GRID';
  };

  window.getWhiteLabelOrgId = function () {
    return resolvedBrand.orgIds[appEnv];
  };

  window.getIsSSO = function () {
    return Boolean(resolvedBrand.isSSO);
  };

  return resolvedBrand;
}

const APP_CONFIG_URL = '/app-config.json';
const RETRY_DELAY_MS = 1000;

function fetchAppConfig(): Promise<unknown> {
  // no-cache still uses the cached copy, but revalidates it (ETag) on every load.
  return fetch(APP_CONFIG_URL, { cache: 'no-cache' }).then(response => {
    if (!response.ok) {
      throw new Error(`GET ${APP_CONFIG_URL} failed with ${response.status}`);
    }
    return response.json();
  });
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function fetchWithRetry(): Promise<unknown> {
  return fetchAppConfig().catch(() => wait(RETRY_DELAY_MS).then(fetchAppConfig));
}

export function loadAppConfig(): Promise<Brand> {
  return fetchWithRetry().then(appConfig => {
    const { errors, unknownKeys } = validateAppConfig(appConfig);
    if (errors.length) {
      throw new Error(`Invalid ${APP_CONFIG_URL}: ${errors.join('; ')}`);
    }
    if (unknownKeys.length) {
      console.warn(`${APP_CONFIG_URL} has keys this build does not use: ${unknownKeys.join(', ')}`);
    }
    return installAppConfig(appConfig as AppConfig);
  });
}

export function renderStartupError(error: unknown): void {
  console.error('Ministry Grid failed to start:', error);
  const root = document.getElementById('root');
  if (!root) {
    return;
  }
  root.innerHTML = `
    <div style="font-family: Rubik, sans-serif; max-width: 32rem; margin: 20vh auto; padding: 0 16px; text-align: center;">
      <h1 style="font-size: 1.5rem; font-weight: 500;">We couldn't load Ministry Grid</h1>
      <p>Please check your connection and try again.</p>
      <button type="button" onclick="window.location.reload()" style="padding: 8px 20px; cursor: pointer;">Reload</button>
    </div>`;
}
